import { Injectable } from '@angular/core';
import { IBank } from '../interfaces/economy/IBank';

export interface BankAccountValidationResult {
  isValid: boolean;
  message: string;
  resolvedBank: IBank | null;
  resolvedBankName: string;
  normalizedClearingNumber: string;
  normalizedAccountNumber: string;
}

type ValidationType =
  | 'type1Variant1'
  | 'type1Variant2'
  | 'type2Variant1'
  | 'type2Variant2'
  | 'type2Variant3'
  | 'type2Variant4';

interface BankRule {
  bankName: string;
  groupBankId: number;
  clearingRange: string;
  validationType: ValidationType;
  includeFifthClearingDigitInAccount?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SwedishBankAccountValidationService {
  private readonly type1Variant1Weights = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
  private readonly type1Variant2Weights = [1, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
  private readonly type2Variant2Weights = [9, 8, 7, 6, 5, 4, 3, 2, 1];
  private readonly type2Variant4Weights = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];

  private readonly rules: BankRule[] = [
    { bankName: 'Swedbank & Sparbankerna', groupBankId: 1, clearingRange: '7000-7999', validationType: 'type1Variant1' },
    { bankName: 'Swedbank & Sparbankerna', groupBankId: 1, clearingRange: '8000-8999', validationType: 'type2Variant3', includeFifthClearingDigitInAccount: true },
    { bankName: 'Swedbank & Sparbankerna', groupBankId: 1, clearingRange: '9300-9349', validationType: 'type2Variant1' },
    { bankName: 'Handelsbanken', groupBankId: 3, clearingRange: '6000-6999', validationType: 'type2Variant2' },
    { bankName: 'Nordea', groupBankId: 4, clearingRange: '1100-1199', validationType: 'type1Variant1' },
    { bankName: 'Danske Bank', groupBankId: 8, clearingRange: '1200-1399', validationType: 'type1Variant1' },
    { bankName: 'Nordea', groupBankId: 4, clearingRange: '1400-2099', validationType: 'type1Variant1' },
    { bankName: 'Juni Technology AB', groupBankId: 37, clearingRange: '2110-2119', validationType: 'type1Variant2' },
    { bankName: 'Steven AB', groupBankId: 38, clearingRange: '2120-2126', validationType: 'type1Variant1' },
    { bankName: 'Steven AB', groupBankId: 38, clearingRange: '2127-2127', validationType: 'type1Variant1' },
    { bankName: 'Steven AB', groupBankId: 38, clearingRange: '2128-2128', validationType: 'type1Variant1' },
    { bankName: 'Steven AB', groupBankId: 38, clearingRange: '2129-2129', validationType: 'type1Variant1' },
    { bankName: 'Zimpler AB', groupBankId: 39, clearingRange: '2130-2139', validationType: 'type1Variant1' },
    { bankName: 'Trustly Group AB', groupBankId: 40, clearingRange: '2140-2149', validationType: 'type1Variant2' },
    { bankName: 'Revolut Bank UAB', groupBankId: 41, clearingRange: '2150-2159', validationType: 'type2Variant4' },
    { bankName: 'Trade Republic Bank', groupBankId: 42, clearingRange: '2160-2169', validationType: 'type2Variant4' },
    { bankName: 'PPRO Payment Services SA', groupBankId: 43, clearingRange: '2170-2179', validationType: 'type1Variant1' },
    { bankName: 'Alandsbanken', groupBankId: 9, clearingRange: '2300-2399', validationType: 'type1Variant2' },
    { bankName: 'Danske Bank', groupBankId: 8, clearingRange: '2400-2499', validationType: 'type1Variant1' },
    { bankName: 'Nordea', groupBankId: 4, clearingRange: '3000-3299', validationType: 'type1Variant1' },
    { bankName: 'Nordea', groupBankId: 4, clearingRange: '3301-3399', validationType: 'type1Variant1' },
    { bankName: 'Lansforsakringar Bank', groupBankId: 10, clearingRange: '3400-3409', validationType: 'type1Variant1' },
    { bankName: 'Nordea', groupBankId: 4, clearingRange: '3410-3781', validationType: 'type1Variant1' },
    { bankName: 'Nordea', groupBankId: 4, clearingRange: '3783-3999', validationType: 'type1Variant1' },
    { bankName: 'Nordea', groupBankId: 4, clearingRange: '4000-4999', validationType: 'type1Variant2' },
    { bankName: 'SEB', groupBankId: 6, clearingRange: '5000-5999', validationType: 'type1Variant1' },
    { bankName: 'Lansforsakringar Bank', groupBankId: 10, clearingRange: '9020-9029', validationType: 'type1Variant2' },
    { bankName: 'Citibank', groupBankId: 21, clearingRange: '9040-9049', validationType: 'type1Variant2' },
    { bankName: 'Lansforsakringar Bank', groupBankId: 10, clearingRange: '9060-9069', validationType: 'type1Variant1' },
    { bankName: 'Multitude Bank plc.', groupBankId: 47, clearingRange: '9070-9079', validationType: 'type1Variant1' },
    { bankName: 'Nordnet Bank', groupBankId: 45, clearingRange: '9100-9109', validationType: 'type1Variant2' },
    { bankName: 'SEB', groupBankId: 6, clearingRange: '9120-9124', validationType: 'type1Variant1' },
    { bankName: 'SEB', groupBankId: 6, clearingRange: '9130-9149', validationType: 'type1Variant1' },
    { bankName: 'Skandiabanken', groupBankId: 11, clearingRange: '9150-9169', validationType: 'type1Variant2' },
    { bankName: 'Ikanobanken', groupBankId: 46, clearingRange: '9170-9179', validationType: 'type1Variant1' },
    { bankName: 'Danske Bank', groupBankId: 8, clearingRange: '9180-9189', validationType: 'type2Variant1' },
    { bankName: 'DNB Bank', groupBankId: 12, clearingRange: '9190-9199', validationType: 'type1Variant2' },
    { bankName: 'Marginalen Bank', groupBankId: 15, clearingRange: '9230-9239', validationType: 'type1Variant1' },
    { bankName: 'SBAB', groupBankId: 13, clearingRange: '9250-9259', validationType: 'type1Variant1' },
    { bankName: 'ICA Banken', groupBankId: 14, clearingRange: '9270-9279', validationType: 'type1Variant1' },
    { bankName: 'Resurs Bank', groupBankId: 16, clearingRange: '9280-9289', validationType: 'type1Variant1' },
    { bankName: 'Landshypotek', groupBankId: 17, clearingRange: '9390-9399', validationType: 'type1Variant2' },
    { bankName: 'ForeX Bank', groupBankId: 18, clearingRange: '9400-9449', validationType: 'type1Variant1' },
    { bankName: 'Santander Consumer Bank', groupBankId: 19, clearingRange: '9460-9469', validationType: 'type1Variant1' },
    { bankName: 'BNP Paribas', groupBankId: 20, clearingRange: '9470-9479', validationType: 'type1Variant2' },
    { bankName: 'Nordea (Plusgirot)', groupBankId: 44, clearingRange: '9500-9549', validationType: 'type2Variant3' },
    { bankName: 'Avanza Bank', groupBankId: 22, clearingRange: '9550-9564', validationType: 'type1Variant2' },
    { bankName: 'Avanza Bank', groupBankId: 22, clearingRange: '9565-9569', validationType: 'type2Variant4' },
    { bankName: 'Sparbanken Syd', groupBankId: 23, clearingRange: '9570-9579', validationType: 'type2Variant1' },
    { bankName: 'Aion Bank', groupBankId: 24, clearingRange: '9580-9589', validationType: 'type1Variant1' },
    { bankName: 'EP Bank', groupBankId: 25, clearingRange: '9590-9599', validationType: 'type1Variant2' },
    { bankName: 'Banking Circle', groupBankId: 26, clearingRange: '9600-9609', validationType: 'type2Variant4' },
    { bankName: 'Lan & Spar Bank', groupBankId: 27, clearingRange: '9630-9639', validationType: 'type1Variant1' },
    { bankName: 'NOBA Bank Group', groupBankId: 28, clearingRange: '9640-9649', validationType: 'type1Variant2' },
    { bankName: 'MedMera Bank', groupBankId: 29, clearingRange: '9650-9659', validationType: 'type1Variant2' },
    { bankName: 'Svea Bank', groupBankId: 30, clearingRange: '9660-9669', validationType: 'type1Variant2' },
    { bankName: 'JAK Medlemsbank', groupBankId: 31, clearingRange: '9670-9679', validationType: 'type1Variant2' },
    { bankName: 'Enity Bank Group', groupBankId: 32, clearingRange: '9680-9689', validationType: 'type1Variant1' },
    { bankName: 'Ekobanken', groupBankId: 33, clearingRange: '9700-9709', validationType: 'type1Variant2' },
    { bankName: 'Lunar Bank', groupBankId: 34, clearingRange: '9710-9719', validationType: 'type1Variant2' },
    { bankName: 'Northmill AB', groupBankId: 35, clearingRange: '9750-9759', validationType: 'type1Variant2' },
    { bankName: 'Klarna Bank', groupBankId: 36, clearingRange: '9780-9789', validationType: 'type1Variant2' },
    { bankName: 'Nordea (Plusgirot)', groupBankId: 44, clearingRange: '9960-9969', validationType: 'type2Variant3' }
  ];

  validate(
    banks: IBank[] | null | undefined,
    clearingNumber: string | null | undefined,
    accountNumber: string | null | undefined,
    personalNumber?: string | null
  ): BankAccountValidationResult {
    const normalizedClearingNumber = this.normalizeDigits(clearingNumber);
    const normalizedAccountNumber = this.normalizeDigits(accountNumber);
    const normalizedPersonalNumber = this.normalizeDigits(personalNumber);

    if (!normalizedAccountNumber) {
      return this.invalid('Kontonummer saknas.', normalizedClearingNumber, normalizedAccountNumber);
    }

    if (!normalizedClearingNumber) {
      return this.validateNordeaPersonkonto(banks, normalizedAccountNumber, normalizedPersonalNumber);
    }

    if (normalizedClearingNumber.length < 4 || normalizedClearingNumber.length > 5) {
      return this.invalid('Clearingnumret måste bestå av 4 eller 5 siffror.', normalizedClearingNumber, normalizedAccountNumber);
    }

    const clearingKey = normalizedClearingNumber.substring(0, 4);
    if (clearingKey === '3300' || clearingKey === '3782') {
      return this.validateNordeaPersonkonto(banks, normalizedAccountNumber, normalizedPersonalNumber, normalizedClearingNumber);
    }

    const rule = this.rules.find((item) => this.matchesRange(clearingKey, item.clearingRange));
    if (!rule) {
      return this.invalid('Clearingnumret kunde inte kopplas till en bank som systemet kan validera.', normalizedClearingNumber, normalizedAccountNumber);
    }

    const resolvedBank = (banks || []).find((bank) => bank.Id === rule.groupBankId) || null;
    if (!resolvedBank || resolvedBank.IsInternational) {
      return this.invalid('Banken är inte godkänd för svenska bankkonton.', normalizedClearingNumber, normalizedAccountNumber);
    }

    let isValid = false;

    switch (rule.validationType) {
      case 'type1Variant1':
        isValid = this.validateType1(normalizedClearingNumber, normalizedAccountNumber, this.type1Variant1Weights);
        break;
      case 'type1Variant2':
        isValid = this.validateType1(normalizedClearingNumber, normalizedAccountNumber, this.type1Variant2Weights, true);
        break;
      case 'type2Variant1':
        isValid = normalizedAccountNumber.length >= 7 && normalizedAccountNumber.length <= 10
          && this.validateMod10(normalizedAccountNumber.padStart(10, '0'));
        break;
      case 'type2Variant2':
        isValid = normalizedAccountNumber.length >= 7 && normalizedAccountNumber.length <= 9
          && this.validateMod11(normalizedAccountNumber.padStart(9, '0'), this.type2Variant2Weights);
        break;
      case 'type2Variant3': {
        const accountCandidates = normalizedAccountNumber.length >= 7 && normalizedAccountNumber.length <= 10
          ? [normalizedAccountNumber]
          : [];
        if (accountCandidates.length > 0 && rule.includeFifthClearingDigitInAccount && normalizedClearingNumber.length === 5) {
          accountCandidates.push(normalizedClearingNumber.substring(4, 5) + normalizedAccountNumber);
        }

        isValid = accountCandidates
          .filter((candidate, index) => candidate && accountCandidates.indexOf(candidate) === index)
          .some((candidate) => candidate.length <= 10 && this.validateMod10(candidate.padStart(10, '0')));
        break;
      }
      case 'type2Variant4':
        isValid = normalizedAccountNumber.length >= 7 && normalizedAccountNumber.length <= 10
          && this.validateMod11(normalizedAccountNumber.padStart(10, '0'), this.type2Variant4Weights);
        break;
    }

    const resolvedBankName = resolvedBank.Name;

    if (!isValid) {
      return this.invalid(`Kontonumret är inte giltigt för ${resolvedBankName}.`, normalizedClearingNumber, normalizedAccountNumber, resolvedBank, resolvedBankName);
    }

    return {
      isValid: true,
      message: '',
      resolvedBank,
      resolvedBankName,
      normalizedClearingNumber,
      normalizedAccountNumber
    };
  }

  resolveBankFromClearing(
    banks: IBank[] | null | undefined,
    clearingNumber: string | null | undefined
  ): Pick<BankAccountValidationResult, 'resolvedBank' | 'resolvedBankName' | 'normalizedClearingNumber'> {
    const normalizedClearingNumber = this.normalizeDigits(clearingNumber);
    if (!normalizedClearingNumber || normalizedClearingNumber.length < 4 || normalizedClearingNumber.length > 5) {
      return {
        resolvedBank: null,
        resolvedBankName: '',
        normalizedClearingNumber
      };
    }

    const clearingKey = normalizedClearingNumber.substring(0, 4);
    if (clearingKey === '3300' || clearingKey === '3782') {
      return {
        resolvedBank: (banks || []).find((bank) => bank.Id === 5) || null,
        resolvedBankName: 'Nordea personkonto',
        normalizedClearingNumber
      };
    }

    const rule = this.rules.find((item) => this.matchesRange(clearingKey, item.clearingRange));
    if (!rule) {
      return {
        resolvedBank: null,
        resolvedBankName: '',
        normalizedClearingNumber
      };
    }

    return {
      resolvedBank: (banks || []).find((bank) => bank.Id === rule.groupBankId && !bank.IsInternational) || null,
      resolvedBankName: ((banks || []).find((bank) => bank.Id === rule.groupBankId && !bank.IsInternational) || null)?.Name || '',
      normalizedClearingNumber
    };
  }

  private validateNordeaPersonkonto(
    banks: IBank[] | null | undefined,
    accountNumber: string,
    personalNumber?: string | null,
    clearingNumber = '3300'
  ): BankAccountValidationResult {
    const nordeaPersonkontoBank = (banks || []).find((bank) => bank.Id === 5) || null;

    if (!nordeaPersonkontoBank || nordeaPersonkontoBank.IsInternational) {
      return this.invalid('Banken är inte godkänd för svenska bankkonton.', clearingNumber, accountNumber);
    }

    if (!personalNumber || personalNumber.length < 10 || accountNumber.length !== 10 || personalNumber.slice(-10) !== accountNumber) {
      return this.invalid(
        'Kontonumret måste matcha personnumrets sista 10 siffror för Nordea personkonto.',
        clearingNumber,
        accountNumber,
        nordeaPersonkontoBank,
        'Nordea personkonto'
      );
    }

    if (!this.validateMod10(accountNumber.padStart(10, '0'))) {
      return this.invalid('Kontonumret är inte giltigt för Nordea personkonto.', clearingNumber, accountNumber, nordeaPersonkontoBank, 'Nordea personkonto');
    }

    return {
      isValid: true,
      message: '',
      resolvedBank: nordeaPersonkontoBank,
      resolvedBankName: 'Nordea personkonto',
      normalizedClearingNumber: clearingNumber,
      normalizedAccountNumber: accountNumber
    };
  }

  private validateType1(clearingNumber: string, accountNumber: string, weights: number[], includeFirstClearingDigit = false): boolean {
    if (accountNumber.length !== 7 && accountNumber.length !== 10) {
      return false;
    }

    const significantAccountNumber = accountNumber.length === 7 ? accountNumber : accountNumber.slice(-7);
    const clearingDigits = includeFirstClearingDigit ? clearingNumber.substring(0, 4) : clearingNumber.substring(1, 4);
    return this.validateMod11(clearingDigits + significantAccountNumber, weights);
  }

  private validateMod10(number: string): boolean {
    let sum = 0;
    let weight = 1;

    for (let index = number.length - 1; index >= 0; index--) {
      let product = Number(number[index]) * weight;
      if (product > 9) {
        product -= 9;
      }

      sum += product;
      weight = weight === 1 ? 2 : 1;
    }

    return sum % 10 === 0;
  }

  private validateMod11(number: string, weights: number[]): boolean {
    if (number.length !== weights.length) {
      return false;
    }

    let sum = 0;
    for (let index = 0; index < number.length; index++) {
      sum += Number(number[index]) * weights[index];
    }

    return sum % 11 === 0;
  }

  private matchesRange(clearingNumber: string, range: string): boolean {
    if (!range.includes('-')) {
      return clearingNumber === range;
    }

    const [start, end] = range.split('-').map((value) => Number(value));
    const current = Number(clearingNumber);
    return current >= start && current <= end;
  }

  private normalizeDigits(value: string | null | undefined): string {
    return (value || '').replace(/\D+/g, '');
  }

  private invalid(message: string, normalizedClearingNumber: string, normalizedAccountNumber: string, resolvedBank: IBank | null = null, resolvedBankName = ''): BankAccountValidationResult {
    return {
      isValid: false,
      message,
      resolvedBank,
      resolvedBankName,
      normalizedClearingNumber,
      normalizedAccountNumber
    };
  }
}
